import {parseInteger} from '@benchling/js-utils';
import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {EventConstants} from 'constants/EventConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import FolderItemState from 'filestate/FolderItemState';
import FileStates from 'FileStates';
import {HoistedTag} from 'inventory/InventoryFileConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import MutationTrackingMap from 'util/MutationTrackingMap';

function _addToMap<V>(map: Map<string, Array<V>>, key: string, value: V): void {
  const oldList = map.get(key)!;
  const list = reactUpdate(oldList, {$push: [value]});
  map.set(key, list);
}

// Returns false if the element was not found
function _removeFromMap<V>(map: Map<string, Array<V>>, key: string, filter: (v: V) => boolean): boolean {
  const oldList = map.get(key)!;
  // eslint-disable-next-line no-restricted-syntax
  const itemIndex = _.findIndex(oldList, (info) => filter(info));
  if (itemIndex === -1) {
    return false;
  }
  const list = reactUpdate(oldList, {$splice: [[itemIndex, 1]]});
  map.set(key, list);
  return true;
}

function _updateMap<V>(
  map: Map<string, Array<V>>,
  key: string,
  updateTemplate: Spec<V>,
  filter: (v: V) => boolean
): void {
  const oldList = map.get(key)!;
  // eslint-disable-next-line no-restricted-syntax
  const itemIndex = _.findIndex(oldList, (info) => filter(info));
  // TS gets confused by the reactUpdate types, so just use any for now.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const item = reactUpdate(oldList[itemIndex], updateTemplate as any);
  const list = reactUpdate(oldList, {$splice: [[itemIndex, 1, item]]});
  map.set(key, list);
}

function _prepareHoistedTagsForStore(hoistedTagsGraphById: {
  [id: string]: Array<HoistedTag>;
}): Array<HoistedTag> {
  const hoistedTags: Array<HoistedTag> = [];
  Object.entries(hoistedTagsGraphById).forEach(([tagSchemaGraphId, schemaTags]) => {
    schemaTags.forEach((schemaTag) => {
      const hoistedTag: HoistedTag = {...schemaTag, tagSchemaGraphId: parseInteger(tagSchemaGraphId)!};
      hoistedTags.push(hoistedTag);
    });
  });
  return hoistedTags;
}

/**
 * Store that is responsible for metadata of inventory files.
 */
class InventoryFileStore extends Store {
  bioentityAliasInfosMap!: MutationTrackingMap<string, Array<BioentityAliasInfo>>;
  hoistedTagsMap!: Map<string, Array<HoistedTag>>;
  isLoadingAliases!: boolean;
  isLoadingTags!: boolean;

  fileStates!: FileStates;

  setFileStates(fileStates: FileStates): void {
    this.fileStates = fileStates;
  }

  override resetState(): void {
    this.bioentityAliasInfosMap = new MutationTrackingMap();
    this.hoistedTagsMap = new Map();
    this.isLoadingAliases = false;
    this.isLoadingTags = false;
  }

  getIsLoadingAliases(): boolean {
    return this.isLoadingAliases;
  }

  getIsLoadingTags(): boolean {
    return this.isLoadingTags;
  }

  getBioentityAliasInfos(bioentityId: string): Array<BioentityAliasInfo> | undefined {
    return this.bioentityAliasInfosMap.get(bioentityId);
  }

  getHoistedTags(bioentityId: string): Array<HoistedTag> | undefined {
    return this.hoistedTagsMap.get(bioentityId);
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.INVENTORY_FILE_LOAD:
        {
          const {fileId, bioentityAliasInfos} = action;
          this.bioentityAliasInfosMap.set(fileId, bioentityAliasInfos);
          this.emitChange();
        }
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_LOAD:
        this.isLoadingAliases = true;
        this.emitChange();
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED:
        {
          this.isLoadingAliases = false;
          const {bioentityAliasInfosById} = action;
          for (const fileId of Object.keys(bioentityAliasInfosById)) {
            this.bioentityAliasInfosMap.set(fileId, bioentityAliasInfosById[fileId]);
          }
        }
        this.emitChange();
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_LOAD_FROM_GRAPHQL:
        {
          const {bioentityAliasInfosById} = action;
          for (const fileId of Object.keys(bioentityAliasInfosById)) {
            if (this.bioentityAliasInfosMap.has(fileId)) {
              const existingBAINames = new Set(this.getBioentityAliasInfos(fileId)!.map((bai) => bai.alias));
              for (const bioentityAlias of bioentityAliasInfosById[fileId]) {
                if (!existingBAINames.has(bioentityAlias.alias)) {
                  _addToMap(this.bioentityAliasInfosMap, fileId, bioentityAlias);
                }
              }
            } else {
              this.bioentityAliasInfosMap.set(fileId, bioentityAliasInfosById[fileId]);
            }
          }
        }
        this.emitChange();
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_ADD_COMPLETED:
        {
          const {fileId, bioentityAliasInfo} = action;
          if (!this.bioentityAliasInfosMap.has(fileId)) {
            break;
          }
          _addToMap(this.bioentityAliasInfosMap, fileId, bioentityAliasInfo);
          this.emitChange();
        }
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_DELETE_COMPLETED:
        {
          const {fileId, alias} = action;
          if (!this.bioentityAliasInfosMap.has(fileId)) {
            break;
          }
          if (
            _removeFromMap(
              this.bioentityAliasInfosMap,
              fileId,
              (bioentityAliasInfo) => bioentityAliasInfo.alias === alias
            )
          ) {
            this.emitChange();
          }
        }
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_UPDATE_COMPLETED:
        {
          const {fileId, oldAlias, alias} = action;
          if (!this.bioentityAliasInfosMap.has(fileId)) {
            break;
          }
          _updateMap(
            this.bioentityAliasInfosMap,
            fileId,
            {alias: {$set: alias}},
            (bioentityAliasInfo) => bioentityAliasInfo.alias === oldAlias
          );
          this.emitChange();
        }
        break;

      case EventConstants.BIOENTITY_ALIAS_INFO_UPDATE_FAILED:
        this.emitChange();
        break;

      case EventConstants.FILE_TAGS_UPDATE_STARTED:
        this.isLoadingTags = true;
        this.emitChange();
        break;

      case EventConstants.FILE_TAGS_UPDATED:
      case EventConstants.FILE_TAGS_UPDATE_FAILED:
        this.isLoadingTags = false;
        this.emitChange();
        break;

      case EventConstants.HOISTED_TAGS_FETCH_COMPLETED:
        {
          const {fileId, hoistedTagsGraphById} = action;
          this.hoistedTagsMap.set(fileId, _prepareHoistedTagsForStore(hoistedTagsGraphById));
          this.emitChange();
        }
        break;

      case EventConstants.INVENTORY_FILES_UPDATE_REGISTRY_COLLECTIONS:
        {
          const {newFileInfo} = action;
          for (const fileId of Object.keys(newFileInfo)) {
            this.bioentityAliasInfosMap.set(fileId, newFileInfo[fileId].bioentityAliasInfos);
          }
          this.emitChange();
        }
        break;

      case EventConstants.FILE_TAG_SCHEMA_UPDATED:
        {
          const {fileId, tagSchema, tags} = action;
          const fileState = this.fileStates.get(fileId);
          if (fileState != null) {
            (fileState as FolderItemState).model().setTagSchema(tagSchema);
            (fileState as FolderItemState).model().setTags(tags);
          }
          this.emitChange();
        }
        break;

      case EventConstants.FILE_BROWSER_RENAME_FILE:
        {
          const {id, bioentityAliasInfos} = action;
          if (bioentityAliasInfos) {
            this.bioentityAliasInfosMap.set(id, bioentityAliasInfos);
          }
          this.emitChange();
        }
        break;

      case EventConstants.FILE_BROWSER_RENAME_FILE_FAILED:
        this.emitChange();
        break;

      default:
        break;
    }
    return true;
  }
}

export default new InventoryFileStore(AppDispatcher);
